import * as THREE from 'three'
import TweenLite from 'gsap/TweenLite'
import { Power4 } from 'gsap/EasePack'

import Cursor from './Cursor'
import Grid from './Grid'
import Scan from './Scan'
import Scan2 from './Scan2'
import White from './White'
import Models from './Models'
import EventEmitter from './EventEmitter'

import ShaderPass from './EffectComposer/ShaderPass'
import EffectComposer from './EffectComposer'

export default class SliderExperience extends EventEmitter {
    /**
     * Constructor
     */
    constructor() {
        super()

        this.setResources()
        this.setTime()
        this.setSizes()
        this.setCursor()
        this.setRenderer()
        this.setScenes()
        this.setComposer()

        this.update()
        this.resize()
    }
    
    /**
     * Set resources
     */
    setResources() {
        this.resources = {}
        this.resources.items = {}
        
        // Progress
        this.resources.progress = {}
        this.resources.progress.toLoad = 0
        this.resources.progress.loaded = 0
        this.resources.progress.ratio = 0
        
        // Load callback
        this.resources.load = () => {
            this.resources.progress.loaded++
            
            this.resources.progress.ratio = this.resources.progress.loaded / this.resources.progress.toLoad
            
            if (this.resources.progress.ratio > 1) {
                this.resources.progress.ratio = 1
            }
            
            window.requestAnimationFrame(() => {
                this.trigger('loadProgress', [this.resources])
            })
            
            if (this.resources.progress.ratio === 1) {
                window.requestAnimationFrame(() => {
                    this.trigger('loadEnd', [this.resources])
                })
            }
        }
        
        // Dispose function
        this.resources.dispose = () => {
            this.resources.items.tunnelDefaultTexture.dispose()
            this.resources.items.tunnelScannedTexture.dispose()
            this.resources.items.tunnelAlphaTexture.dispose()
        }
        
        // Load textures
        const textureLoader = new THREE.TextureLoader()
        
        this.resources.progress.toLoad++
        this.resources.items.tunnelDefaultTexture = textureLoader.load('/static/3d/tunnel-test-1-texture-default.jpg', this.resources.load)
        
        this.resources.progress.toLoad++
        this.resources.items.tunnelScannedTexture = textureLoader.load('/static/3d/tunnel-test-1-texture-scanned.jpg', this.resources.load)
        
        this.resources.progress.toLoad++
        this.resources.items.tunnelAlphaTexture = textureLoader.load('/static/3d/tunnel-test-1-texture-alpha.jpg', this.resources.load)
        
        window.requestAnimationFrame(() => {
            this.trigger('loadStart')
            this.trigger('loadStart')
            this.trigger('loadStart')
        })
    }

    /**
     * Set time
     */
    setTime() {
        this.time = {}
        this.time.start = +new Date()
        this.time.elapsed = 0
        this.time.current = this.time.start
        this.time.delta = 16
    }

    /**
     * Set cursor
     */
    setCursor() {
        this.cursor = new Cursor()
    }

    /**
     * Set resize
     */
    setSizes() {
        this.sizes = {}
        this.sizes.width = 1920
        this.sizes.height = 1080

        window.addEventListener('resize', () => {
            this.resize()
        })
    }

    /**
     * Set renderer
     */
    setRenderer() {
        // Renderer
        this.renderer = new THREE.WebGLRenderer({
            stencil: false,
            depth: true
        })
        this.renderer.setClearColor(0x000000)
        // this.renderer.sortObjects = false
    }

    /**
     * Set composer
     */
    setComposer() {
        this.composer = new EffectComposer(this.renderer)

        // Distortion pass
        this.distortionShaderPass = new ShaderPass({
            uniforms:
            {
                uTextureA: { type: 't', value: null },
                uTextureB: { type: 't', value: null },
                uCurveMultiplier: { type: 'f', value: 0.02 },
                uFilmEffect: { type: 'f', value: 0 },
                uOverlayEffect: { type: 'f', value: 0 },

                uOffset: { type: 'f', value: 0 },
                uDirection: { type: 'f', value: 1 }
            },
            vertexShader: `
                varying vec2 vUv;

                void main()
                {
                    vUv = uv;
                    gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
                }
            `,
            fragmentShader: `
                uniform sampler2D uTextureA;
                uniform sampler2D uTextureB;
                uniform float uCurveMultiplier;
                uniform float uFilmEffect;
                uniform float uOverlayEffect;

                uniform float uOffset;

                varying vec2 vUv;

                void main()
                {
                    // Target UV based on curve
                    float powStrength = 10.0;
                    float multiplierStrength = 0.06;

                    vec2 uvTarget = vUv;
                    uvTarget.x -= pow(abs((uCurveMultiplier < 0.0 ? 1.0 : 0.0) - vUv.x), powStrength) * multiplierStrength * uCurveMultiplier;
                    uvTarget.x -= uCurveMultiplier * 0.5;
                    uvTarget.x += sin(vUv.y * 3.14) * uCurveMultiplier;

                    // Texture A color
                    vec2 uvA = uvTarget;
                    uvA.x += uOffset;
                    vec4 colorA = texture2D(uTextureA, uvA);

                    // Texture B color
                    vec2 uvB = uvTarget;
                    uvB.x -= uOffset > 0.0 ? 1.0 - uOffset : -(1.0 + uOffset);
                    vec4 colorB = texture2D(uTextureB, uvB);

                    // Final color
                    vec4 color;
                    if(uOffset > 0.0 ? uvTarget.x > 1.0 - uOffset: uvTarget.x < -uOffset)
                    {
                        color = colorB;
                    }
                    else
                    {
                        color = colorA;
                    }

                    // Film effect
                    if(vUv.y < 0.5 * uFilmEffect || vUv.y > 1.0 - (0.5 * uFilmEffect))
                    {
                        color.rgb = vec3(0.0);
                    }
                    
                    // Overlay effect
                    color.rgb *= uOverlayEffect;

                    gl_FragColor = color;
                }
            `
        })

        this.distortionShaderPass.uniforms.uTextureA.value = this.scenes.active.texture
        this.distortionShaderPass.uniforms.uTextureB.value = this.scenes.leaving.texture
        this.distortionShaderPass.uniforms.uCurveMultiplier.value = 0
        this.distortionShaderPass.uniforms.uOffset.value = 0
        this.distortionShaderPass.uniforms.uFilmEffect.value = 0
        this.distortionShaderPass.uniforms.uOverlayEffect.value = 1

        this.distortionShaderPass.renderToScreen = true

        this.composer.addPass(this.distortionShaderPass)
    }

    setScenes() {
        this.scenes = {}
        this.scenes.all = {
            scan: new Scan({
                resources: this.resources,
                time: this.time,
                sizes: this.sizes,
                cursor: this.cursor,
                renderer: this.renderer,
                hasTexture: true
            }),
            scan2: new Scan2({
                resources: this.resources,
                time: this.time,
                sizes: this.sizes,
                cursor: this.cursor,
                renderer: this.renderer,
                hasTexture: true
            }),
            grid: new Grid({
                time: this.time,
                sizes: this.sizes,
                cursor: this.cursor,
                renderer: this.renderer,
                hasTexture: true
            }),
            white: new White({
                time: this.time,
                sizes: this.sizes,
                cursor: this.cursor,
                renderer: this.renderer,
                hasTexture: true
            }),
            models: new Models({
                time: this.time,
                sizes: this.sizes,
                cursor: this.cursor,
                renderer: this.renderer,
                hasTexture: true
            })
        }

        this.scenes.pagination = ['grid', 'white', 'scan', 'models', 'scan2', 'grid']
        this.scenes.index = 0

        // Defaults
        this.scenes.active = this.scenes.all[this.scenes.pagination[0]]
        this.scenes.leaving = this.scenes.all[this.scenes.pagination[0]]
    }

    goTo(index) {
        // Same index
        if (index === this.scenes.index) {
            return
        }

        // Same scene
        const sceneName = this.scenes.pagination[index]

        if (sceneName !== this.scenes.active.name) {
            // Direction
            const direction = index > this.scenes.index ? 'right' : 'left'

            // Update scenes
            this.scenes.leaving = this.scenes.active
            this.scenes.active = this.scenes.all[sceneName]

            // Update shader textures
            this.distortionShaderPass.uniforms.uTextureA.value = this.scenes.active.texture
            this.distortionShaderPass.uniforms.uTextureB.value = this.scenes.leaving.texture

            // Animate uniforms
            this.distortionShaderPass.uniforms.uOffset.value = direction === 'left' ? 1 : -1
            let oldOffset = this.distortionShaderPass.uniforms.uOffset.value
            TweenLite.killTweensOf(this.distortionShaderPass.uniforms.uOffset)
            TweenLite.to(this.distortionShaderPass.uniforms.uOffset, 1.8, {
                value: 0,
                ease: Power4.easeInOut,
                onUpdate: () => {
                    const diff = this.distortionShaderPass.uniforms.uOffset.value - oldOffset
                    oldOffset = this.distortionShaderPass.uniforms.uOffset.value

                    this.distortionShaderPass.uniforms.uCurveMultiplier.value = diff * 0.5
                }
            })
        }

        // Save index
        this.scenes.index = index
    }

    /**
     * Resize
     */
    resize() {
        this.sizes.width = window.innerWidth
        this.sizes.height = window.innerHeight

        // Resize cursor
        this.cursor.resize(this.sizes.width, this.sizes.height)

        // Resize scenes
        this.scenes.all.grid.resize()
        this.scenes.all.scan.resize()
        this.scenes.all.scan2.resize()
        this.scenes.all.models.resize()
        this.scenes.all.white.resize()

        // Renderer / Composer
        this.renderer.setSize(this.sizes.width, this.sizes.height)
        this.composer.setSize(this.sizes.width, this.sizes.height)
    }

    /**
     * Update
     */
    update() {
        this.raf = window.requestAnimationFrame(() => {
            this.update()
        })

        const time = +new Date()
        this.time.elapsed = time - this.time.start
        this.time.delta = time - this.time.current
        this.time.current = time

        if (this.time.delta > 60) {
            this.time.delta = 60
        }

        // Update scenes
        this.scenes.active.update()

        // Composer
        this.composer.render()
    }

    dispose() {
        // RAF
        window.cancelAnimationFrame(this.raf)

        // Resize
        window.removeEventListener('resize', this.resize)
        
        // Resources
        this.resources.dispose()

        // Cursor
        this.cursor.dispose()

        // Scenes
        this.scenes.all.grid.dispose()
        this.scenes.all.scan.dispose()
        this.scenes.all.scan2.dispose()
        this.scenes.all.models.dispose()
        this.scenes.all.white.dispose()

        // Renderer
        this.renderer.dispose()
    }
}



// WEBPACK FOOTER //
// ./src/scripts/libs/experience/SliderExperience.js